import { Button, Checkbox, Flex, List, Skeleton, Space, Typography, message } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import _ from 'lodash';
import { LoadingOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLocationCrosshairs } from '@fortawesome/free-solid-svg-icons';
import GeneralHelper from '../../../helpers/general';
import MapDirectionsForm from '../../../components/MapDirectionsForm';
import AccessibilityOptions from '../../../constants/AccessibilityOptions';
import NearestSearchOptions, { getNearestSearchOption, isNearestSearchOption } from '../../../constants/NearestSearchOptions';
import CurrentLocationOption, { isCurrentLocationOption } from '../../../constants/CurrentLocationOption';
import useIsMobile from '../../../helpers/customHooks/useIsMobile';
import { useSharedIndoorPositioning } from '../../../contexts/IndoorPositioning';
import ContentDrawer from '../../../components/ContentDrawer';


const { Text, Title, Paragraph } = Typography;

// A route endpoint the API can actually be asked about: a real location, or the user's own
// position. The nearest-search sentinels are neither until they have resolved to a location.
const isRoutableEndpoint = value => isCurrentLocationOption(value) || GeneralHelper.isObjectId(value);

// Turns a field value into the query parameters for that end of the route. Current Location
// travels as the coordinates it was snapshotted at; everything else as its id.
const routeEndpointParams = (value, field, origin) => {
	if (!isCurrentLocationOption(value)) return { [field]: value };

	return {
		[`${field}_longitude`]: origin.longitude,
		[`${field}_latitude`]: origin.latitude,
		[`${field}_building_floor_id`]: origin.building_floor_id
	};
};

const OUTDOOR_FIX_MESSAGE = 'We can see roughly where you are, but not which floor you are on. Move indoors, or pick a starting point.';

const DirectionsPage = (props) => {
	const {
		pageProps,
		getLocations,
		getNearest,
		getDirections,
		getLocationDetailsById,
		getBuildingFloorGeojson,
		locationDetailPageProps,
		getBuildingFloorNavNodes,
		getLocationBuildingFloorsById,
		resetPage,
		resetMeta
	} = props;

	const { location_details, loading_details } = locationDetailPageProps;
	const { directions, loading_directions, locations, nearest_location, loading_nearest, loading_locations, total, meta } = pageProps || {};

	const query = GeneralHelper.getParamsFromUrl();
	const { id, building_floor_id } = query || null;

	const history = useHistory();
	const location = useLocation();

	const [page, setPage] = useState(1);
	const [pathInfo, setPathInfo] = useState(null);
	const [titleHeight, setTitleHeight] = useState(0);
	const [searchWords, setSearchWords] = useState([]);
	const [focusedField, setFocusedField] = useState(null); // end || start
	const [locationsState, setLocationsState] = useState(null);
	const [locationsOptions, setLocationsOptions] = useState(null);
	const [buildingFloorId, setBuildingFloorId] = useState(null);
	const [directionDetails, setDirectionDetails] = useState(null);
	const [directionsNotFound, setDirectionsNotFound] = useState(false);
	const [startEnd, setStartEnd] = useState({ start: undefined, end: undefined });
	const [locationName, setLocationName] = useState(null);
	const [activeIndex, setActiveIndex] = useState(0);
	const [accessibilityOptions, setAccessibilityOptions] = useState({
		is_wheelchair_accessible: false,
		exclude_escalator: false
	});

	// Where the user was when they asked. Deliberately a snapshot rather than a follow: positions
	// stream at 1 Hz, and recomputing the route under the user every second would make the panel
	// unreadable and hammer an uncached Dijkstra. Only picking the field again or "Recalculate"
	// moves it.
	const [positionSnapshot, setPositionSnapshot] = useState(null);
	// A nearest search waiting for its origin to become routable (it may have been picked before
	// the other field, or before positioning produced a fix).
	const [pendingNearest, setPendingNearest] = useState(null);

	const timeoutRef = useRef(null);
	const directionsFormRef = useRef(null);
	const drawerContainerRef = useRef(null);
	// Whether we have already asked positioning to start for this attempt, so a fix that never
	// resolves to a floor doesn't restart the native session on every 1 Hz update.
	const requestedFixRef = useRef(false);
	// The last route we asked for, to avoid asking again for exactly the same one.
	const lastRequestRef = useRef(null);

	const isMobile = useIsMobile();

	const {
		available: positioningAvailable,
		position: userLocation,
		loading: positioningLoading,
		error: positioningError,
		start: startPositioning
	} = useSharedIndoorPositioning();

	const usesCurrentLocation = isCurrentLocationOption(startEnd.start) || isCurrentLocationOption(startEnd.end);

	// The entries that are not real locations. Current Location is only offered inside the native
	// shell, where indoor positioning actually runs.
	const specialOptions = [
		...(positioningAvailable ? [CurrentLocationOption] : []),
		...NearestSearchOptions
	].map(({ label, value, search_key }) => ({ label, value, search_key }));

	const { start, end } = query || {};


	// ANCHOR handlers
	const handleFormOnClick = (e) => {
		if (e.target.id === 'start' || e.target.id === 'end')
			setFocusedField(e.target.id);
	};

	const handleSwap = () => {
		// A nearest search is measured from the other end, so swapping it changes what was asked
		// for. Current Location swaps freely - "from here" and "to here" are both meaningful.
		if (isNearestSearchOption(startEnd.start) || isNearestSearchOption(startEnd.end)) {
			return;
		}
		setStartEnd({ start: startEnd.end, end: startEnd.start });

		const fields = directionsFormRef.current.getFieldsValue(['start', 'end']);
		const { start, end } = fields;
		directionsFormRef.current.setFieldsValue({ start: end, end: start });
		if (directions && (directions?.paths && directions.paths.length)) {
			history.push(`${location.pathname}?${GeneralHelper.objectToQueryString({ ...query, start: query?.end, end: query?.start, building_floor_id: directions.paths[directions.paths.length - 1].properties.building_floor_id })}`);
		}
	};

	// Takes whichever fields still hold the Current Location sentinel back out of it, so the form
	// doesn't sit on a value it can never route from.
	const clearCurrentLocationFields = (warning) => {
		requestedFixRef.current = false;
		setPositionSnapshot(null);
		setStartEnd(state => ({
			start: isCurrentLocationOption(state.start) ? undefined : state.start,
			end: isCurrentLocationOption(state.end) ? undefined : state.end
		}));

		if (directionsFormRef.current) {
			if (isCurrentLocationOption(startEnd.start)) directionsFormRef.current.setFieldsValue({ start: null });
			if (isCurrentLocationOption(startEnd.end)) directionsFormRef.current.setFieldsValue({ end: null });
		}

		if (warning) message.warning(warning);
	};

	// Re-ask for the route from where the user is standing now rather than where they were when
	// they picked the field. A new snapshot object is what re-triggers the request.
	const handleRecalculate = () => {
		if (positioningLoading) return;

		// An explicit ask goes through even when the user has not moved far enough to change the
		// request, which the routing effect would otherwise recognise and skip.
		lastRequestRef.current = null;

		if (userLocation && GeneralHelper.isObjectId(userLocation.building_floor_id)) {
			setPositionSnapshot({
				longitude: userLocation.longitude,
				latitude: userLocation.latitude,
				building_floor_id: userLocation.building_floor_id
			});
			return;
		}

		// No usable fix in hand - fall back to detecting one, which the snapshot effect picks up.
		requestedFixRef.current = false;
		setPositionSnapshot(null);
	};

	const handleFormOnChange = (e) => {
		clearTimeout(timeoutRef.current);
		const value = e.target.value;

		if (!value.trim()) {
			// If input is empty, clear options
			setLocationsOptions([]);
			return;
		}
		// setSearchWords([...value]);
		timeoutRef.current = setTimeout(() => getLocations({ search: value, page: 1, limit: 9999 }), 300);
	};

	const handleTransitionTo = (item, index) => {
		const { building_floor_id, building_id } = item.properties || {};
		getLocationBuildingFloorsById({ building_id, limit: 50 });
		setActiveIndex(index);
		const queryString = GeneralHelper.objectToQueryString({ ...query, building_floor_id, step: index + 1 });
		history.push(`${location.pathname}?${queryString}`);
	};

	const handleGoBack = () => {
		const queryString = GeneralHelper.objectToQueryString({ id, building_floor_id });
		resetPage();
		history.push(`/location/id?${queryString}`);
	};

	// The query for the current route. Spreading startEnd directly would send the sentinel itself
	// as an id, which is neither a location nor 24 characters long.
	const buildDirectionsRequest = (options) => ({
		...routeEndpointParams(startEnd.start, 'start', positionSnapshot),
		...routeEndpointParams(startEnd.end, 'end', positionSnapshot),
		...options
	});

	const handleAccessibility = (e) => {
		const newOptions = {
			is_wheelchair_accessible: e.includes('is_wheelchair_accessible'),
			exclude_escalator: e.includes('exclude_escalator')
		};

		setAccessibilityOptions(newOptions);

		const queryObject = {
			...query,
			...newOptions
		};
		const queryString = GeneralHelper.objectToQueryString(queryObject);
		history.push(`${location.pathname}?${queryString}`);
		setActiveIndex(0);

		const request = buildDirectionsRequest(newOptions);
		lastRequestRef.current = JSON.stringify(request);
		getDirections(request);
	};

	const onSelect = (data, field_name) => {
		const nearest_option = getNearestSearchOption(data.value);
		const counterpart = startEnd[field_name === 'start' ? 'end' : 'start'];

		if (isCurrentLocationOption(data.value)) {
			// A route from here to here is not a route.
			if (isCurrentLocationOption(counterpart)) {
				directionsFormRef.current.setFieldsValue({ [field_name]: null });
				return;
			}
			// The coordinates are fetched by the snapshot effect, which also covers arriving with
			// the sentinel already in the URL.
			requestedFixRef.current = false;
			setPositionSnapshot(null);
			setStartEnd((state) => ({ ...state, [field_name]: data.value }));
			const queryObject = { ...query, [field_name]: data.value };
			history.push(`${location.pathname}?${GeneralHelper.objectToQueryString(queryObject)}`);
		} else if (nearest_option) {
			// A nearest search needs somewhere to measure from, so at most one of the two fields
			// may hold one - "nearest lift to the nearest toilet" has no starting point.
			if (isNearestSearchOption(counterpart)) {
				directionsFormRef.current.setFieldsValue({ [field_name]: null });
				return;
			}
			setStartEnd((state) => ({ ...state, [field_name]: nearest_option.value }));
			setPendingNearest({ field: field_name, type: nearest_option.type });
		} else {
			setStartEnd((state) => ({ ...state, [field_name]: data.value }));
			const queryObject = { ...query, [field_name]: data.value };
			const queryString = GeneralHelper.objectToQueryString(queryObject);
			history.push(`${location.pathname}?${queryString}`);
		}

		if (directionsFormRef.current) {
			const start_field = directionsFormRef.current.getFieldInstance('start');
			const end_field = directionsFormRef.current.getFieldInstance('end');
			start_field.blur();
			end_field.blur();
		}
	};

	const onClickSelect = (e, field_name) => {
		// Seed the dropdown with Current Location rather than emptying it: routing from where you
		// are standing is the common case and shouldn't require typing. Deliberately only that one
		// - antd does no filtering at all on an empty search, so seeding the nearest searches here
		// would put all four on screen the moment a field is focused, which is not what they are
		// for (they are searched for by name, see MapDirectionsForm's filter).
		setLocationsOptions(positioningAvailable ? [CurrentLocationOption] : null);
		if (field_name === 'start')
			setFocusedField('start');
		if (field_name === 'end')
			setFocusedField('end');

		// getLocationDetailsById(field_name === 'start' ? startEnd.start : startEnd.end);
	};

	const loadMore = () => getLocations({ search: searchWords.join(''), page, limit: 20 });
	// ANCHOR end handlers

	// ANCHOR useEffects
	useEffect(() => {
		const { start, end, is_wheelchair_accessible, exclude_escalator } = query;
		if (start && end) {
			setStartEnd({ start, end });
			return;
			getDirections({
				...(end && { end }),
				...(start && { start }),
				...(is_wheelchair_accessible && { is_wheelchair_accessible }),
				...(exclude_escalator && { exclude_escalator })
			});
		}
	}, []);

	// TODO getLocationNameByNavNodesIds

	useEffect(() => {
		if (id)
			getLocationDetailsById({ id });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id]);

	useEffect(() => {
	}, [start, end]);

	// FIXME location details
	useEffect(() => {
		if (start && location_details) {
			setStartEnd(state => ({ ...state, start }));
			setFocusedField('start');
			// directionsFormRef.current.setFieldValue('start', `${location_details.name}, ${location_details.building_short_name}`);
		}
	}, [start]);

	// // FIXME location details
	useEffect(() => {
		if (end && location_details) {
			setStartEnd(state => ({ ...state, end }));
			setFocusedField('end');
			// directionsFormRef.current.setFieldValue('end', `${location_details.name}, ${location_details.building_short_name}`);
		}
	}, [end]);

	// FIXME
	useEffect(() => {
		if (location_details && !location_details?.is_building) {
			// A field the user has deliberately put a special entry in is not free real estate for
			// the location that happens to be open - Current Location especially, which would
			// otherwise be replaced by the room the directions were opened from.
			const holdsSentinel = value => isCurrentLocationOption(value) || isNearestSearchOption(value);

			if (focusedField === 'start' && !holdsSentinel(startEnd.start)) {
				directionsFormRef.current.setFieldValue('start', `${location_details.name}, ${location_details.building_short_name}`);
				setStartEnd(state => ({ ...state, start: location_details._id }));
			}
			if (focusedField === 'end' && !holdsSentinel(startEnd.end)) {
				directionsFormRef.current.setFieldValue('end', `${location_details.name}, ${location_details.building_short_name}`);
				setStartEnd(state => ({ ...state, end: location_details._id }));
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location_details]);

	// Show "Current Location" in the field whenever the state holds it.
	//
	// Form.Item clones its child and injects `value`, and cloneElement lets that injected prop win
	// over the one the Select declares - so the Select's own `value` only ever shows through when
	// the user picked the entry themselves and antd wrote it into the form. Anything that sets the
	// sentinel behind the form's back (the prefill from a location page, a reloaded URL, a swap)
	// has to put it there explicitly or the field renders empty while the route says otherwise.
	useEffect(() => {
		if (!directionsFormRef.current) return;

		['start', 'end'].forEach(field => {
			if (!isCurrentLocationOption(startEnd[field])) return;
			directionsFormRef.current.setFieldsValue({
				[field]: { value: CurrentLocationOption.value, label: CurrentLocationOption.label }
			});
		});
	}, [startEnd.start, startEnd.end]);

	// Turn "Current Location" into a coordinate we can route from. Covers both picking it in the
	// form and arriving with it already in the URL (a reload, or a link someone shared).
	useEffect(() => {
		if (!usesCurrentLocation || positionSnapshot) return;

		if (!positioningAvailable) {
			clearCurrentLocationFields();
			return;
		}

		if (userLocation && GeneralHelper.isObjectId(userLocation.building_floor_id)) {
			requestedFixRef.current = false;
			setPositionSnapshot({
				longitude: userLocation.longitude,
				latitude: userLocation.latitude,
				building_floor_id: userLocation.building_floor_id
			});
			return;
		}

		// A fix that could not be placed on a floor (outdoors, GPS only) is no use for an indoor
		// route - say so rather than leaving the field stuck on a value that never resolves.
		if (userLocation && !positioningLoading) {
			clearCurrentLocationFields(OUTDOOR_FIX_MESSAGE);
			return;
		}

		if (!requestedFixRef.current && !positioningLoading) {
			requestedFixRef.current = true;
			startPositioning();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [usesCurrentLocation, positionSnapshot, userLocation, positioningAvailable, positioningLoading]);

	// Detection failed or timed out. MapLayout raises the error modal; all that is left here is to
	// let go of a field that can no longer be resolved.
	useEffect(() => {
		if (positioningError && usesCurrentLocation) clearCurrentLocationFields();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [positioningError]);

	// Run a nearest search once it has somewhere to measure from. It can be picked before the
	// other field is filled in, and when that field is Current Location it also has to wait for a
	// fix, so the trigger is an effect rather than the selection itself.
	useEffect(() => {
		if (!pendingNearest) return;

		const counterpart = startEnd[pendingNearest.field === 'start' ? 'end' : 'start'];

		if (isCurrentLocationOption(counterpart)) {
			if (!positionSnapshot) return;
			getNearest({ ...positionSnapshot, type: pendingNearest.type });
		} else if (GeneralHelper.isObjectId(counterpart)) {
			getNearest({ loc_id: counterpart, type: pendingNearest.type });
		} else {
			return;
		}

		setPendingNearest(null);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pendingNearest, positionSnapshot, startEnd]);

	useEffect(() => {
		if (nearest_location) {
			// Replace whichever field still holds the sentinel with the resolved location
			const fieldToUpdate = isNearestSearchOption(startEnd.start)
				? 'start'
				: isNearestSearchOption(startEnd.end) ? 'end' : null;

			if (!fieldToUpdate) return;

			setStartEnd((prev) => ({ ...prev, [fieldToUpdate]: nearest_location._id }));

			directionsFormRef.current.setFieldsValue({
				[fieldToUpdate]: {
					value: nearest_location._id,
					label: `${nearest_location.name}, ${nearest_location.building_name}`
				}
			});

			const queryObject = {
				...query,
				[fieldToUpdate]: nearest_location._id,
				building_floor_id: nearest_location.building_floor_id
			};
			const queryString = GeneralHelper.objectToQueryString(queryObject);
			history.push(`${location.pathname}?${queryString}`);

		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [nearest_location]);

	// useEffect(() => {
	// 	if (location_details && !location_details?.is_building) {
	// 		if (location_details?.building_floor_id && building_floor_id !== location_details?.building_floor_id) {
	// 			// getBuildingFloorGeojson({ building_floor_id: location_details.building_floor_id });
	// 			// getBuildingFloorNavNodes({ building_floor_id: location_details.building_floor_id });
	// 			// setBuildingFloorId(location_details.building_floor_id);
	// 		}
	// 		if (focusedField === 'start') {
	// 			setStartEnd(state => ({ ...state, start: location_details._id }));
	// 		}
	// 		if (focusedField === 'end') {
	// 			setStartEnd(state => ({ ...state, end: location_details._id }));
	// 		}

	// 		history.push(`${location.pathname}?${GeneralHelper.objectToQueryString({ ...query, ...((location_details && location_details?.building_floor_id) && { building_floor_id: location_details.building_floor_id }) })}`);
	// 	}
	// }, [location_details]);


	// // FIXME location details
	// useEffect(() => {
	// 	setDirectionDetails(null);
	// 	if (!location_details) {
	// 		setLocationName('No selected location');
	// 		return;
	// 	};

	// 	if (location_details?.is_building) {
	// 		setLocationName(location_details?.full_name);
	// 		return;
	// 	};

	// 	setLocationName(location_details?.name);

	// 	if (focusedField) {
	// 		if (focusedField === 'start') {
	// 			directionsFormRef.current.setFieldsValue({ start: `${location_details.name}, ${location_details.building_short_name}` });
	// 			setStartEnd(state => ({ ...state, start: location_details._id }));
	// 		}
	// 		if (focusedField === 'end') {
	// 			directionsFormRef.current.setFieldsValue({ end: `${location_details.name}, ${location_details.building_short_name}` });
	// 			setStartEnd(state => ({ ...state, end: location_details._id }));
	// 		}
	// 	}
	// 	const queryObject = { ...query, id: location_details._id, building_floor_id: location_details.building_floor_id };
	// 	const queryString = GeneralHelper.objectToQueryString(queryObject);
	// 	history.push(`${location.pathname}?${queryString}`);
	// 	return;

	// 	// else {
	// 	// 	if (!query.start) {
	// 	// 		setStartEnd({ start: undefined, end: query.end });
	// 	// 		directionsFormRef.current.setFieldsValue({ start: '', end: `${location_details.name}, ${location_details.building_short_name}` });
	// 	// 	}
	// 	// 	if (!query.end) {
	// 	// 		setStartEnd({ start: query.start, end: undefined });
	// 	// 		directionsFormRef.current.setFieldsValue({ start: `${location_details.name}, ${location_details.building_short_name}`, end: '' });
	// 	// 	}
	// 	// }

	// 	// eslint-disable-next-line react-hooks/exhaustive-deps
	// }, [location_details]);

	// FIXME swap issue once switched floor or refreshed
	useEffect(() => {
		resetMeta();

		if (!startEnd || !isRoutableEndpoint(startEnd.start) || !isRoutableEndpoint(startEnd.end)) {
			return;
		}

		// Current Location cannot be asked for until positioning has produced somewhere to ask from.
		if (usesCurrentLocation && !positionSnapshot) {
			return;
		}

		delete query.start;
		delete query.end;

		const queryObject = {
			...query,
			end: startEnd.end,
			start: startEnd.start,
			...accessibilityOptions,
			step: activeIndex + 1
		};

		const request = buildDirectionsRequest(accessibilityOptions);

		// startEnd and positionSnapshot are replaced wholesale by several effects that each settle
		// on the same values, so this effect re-runs on identity alone and would ask for the same
		// route two or three times per selection. Ask once.
		const signature = JSON.stringify(request);
		if (signature === lastRequestRef.current) return;
		lastRequestRef.current = signature;

		const queryString = GeneralHelper.objectToQueryString(queryObject);
		history.push(`${location.pathname}?${queryString}`);
		setActiveIndex(0);

		getDirections(request);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [startEnd, nearest_location, positionSnapshot]);



	useEffect(() => {
		if (directions) {
			const { paths, start, end, start_location_id } = directions || {};
			directionsFormRef.current.setFieldsValue({ start, end });
			if (paths) {
				setPathInfo(() => paths.map((path) => ({ ...path })));
				const initial_path = paths[0];
				const { building_id } = initial_path.properties || {};
				if (building_id) getLocationBuildingFloorsById({ building_id, limit: 50 });
			} else {
				setPathInfo(null);
			}
		} else {
			setPathInfo(null);
		}
	}, [directions]);

	useEffect(() => {
		const { is_wheelchair_accessible, exclude_escalator } = query;
		setAccessibilityOptions({
			is_wheelchair_accessible: is_wheelchair_accessible === 'true',
			exclude_escalator: exclude_escalator === 'true'
		});
	}, []);

	useEffect(() => {
		// setLocationsState(locations);
		if (locations) {
			// setLocationsState(state => {
			// 	if (state)
			// 		return [...state, ...(locations)];
			// });

			const modified_locations = [
				...specialOptions,
				...locations.map((location) => {
					let label;
					if (location?.name && location?.building_name) {
						label = `${location.name}, ${location.building_name}`;
					} else if (location?.full_name) {
						label = location.full_name;
					}
					return {
						label,
						value: location._id,
						search_key: location?.remote_id ? `${label}, ${location.remote_id}` : label
					};
				})
			];

			setLocationsOptions(modified_locations);

			setPage(page + 1);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [locations]);

	useEffect(() => {
		if (GeneralHelper.isObjectId(building_floor_id)) {
			getBuildingFloorGeojson({ building_floor_id: building_floor_id });
			getBuildingFloorNavNodes({ building_floor_id: building_floor_id });

			if (pathInfo && pathInfo.length) {
				const building_floor_ids = pathInfo.map(info => info?.properties?.building_floor_id);
				const index_of_building_floor_id = building_floor_ids.indexOf(building_floor_id, activeIndex);
				const path_info_index = pathInfo.findIndex(info => info?.properties?.building_floor_id === building_floor_id);
				let queryString = {};

				if (index_of_building_floor_id >= 0) {
					setActiveIndex(index_of_building_floor_id);
					queryString = GeneralHelper.objectToQueryString({ ...query, building_floor_id, step: index_of_building_floor_id + 1 });
				} else {
					setActiveIndex(path_info_index);
					queryString = GeneralHelper.objectToQueryString({ ...query, building_floor_id, step: path_info_index + 1 });
				}

				history.push(`${location.pathname}?${queryString}`);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [building_floor_id]);

	// ANCHOR Components
	const renderListItem = (item, index) => {
		const { properties } = item;
		const { start, end, distance, time, location, info } = properties || {};
		let className = 'hkust direction-steps';
		// if (buildingFloorId === item.building_floor_id)

		className = `hkust direction-steps ${activeIndex === index ? 'active' : null}`;
		return (
			<Skeleton
				loading={loading_directions}
				active
				className="list-item-skeleton"
			>
				{
					info ?
						<List.Item className="info">
							<Paragraph style={{ fontSize: 14, margin: 0, lineHeight: 'normal' }}>
								{info}
							</Paragraph>
						</List.Item>
						:
						<List.Item
							className={className}
							onClick={() => handleTransitionTo(item, index)}
						>
							<Flex
								align="center"
								justify="space-between"
								gap={12}
								flex={1}
							>
								<Paragraph style={{ fontSize: 14, margin: 0, lineHeight: 'normal' }}>
									From {start} to {end}
									{location && <Text style={{ display: 'block', opacity: .75 }}>{location}</Text>}
								</Paragraph>
								<span style={{ display: 'flex', flexFlow: 'column', alignItems: 'flex-end', marginInlineStart: 'auto', alignSelf: 'flex-start' }}>
									<span style={{ fontSize: 14, fontWeight: 500, color: '#dc3931', textAlign: 'right', whiteSpace: 'nowrap' }}>
										~{time} min
									</span>
									<span style={{ fontSize: 11 }}>
										{distance} m
									</span>
								</span>
							</Flex>
						</List.Item>
				}
			</Skeleton>
		);
	};

	const renderDrawerTitle = () => {
		return (
			<Title
				level={4}
				style={{ marginBlockEnd: 0 }}
			>
				{loading_directions ?
					'Finding path...'
					:
					(directions && !directions.paths) && meta?.message ? meta.message :
						locationName && <>
							{locationName}
							{
								(location_details?.building_floor_name && location_details?.building_name) &&
								<Text style={{ display: 'block', opacity: .75 }}>
									Floor {location_details?.building_floor_name}, {location_details?.building_name}
								</Text>
							}
						</>
				}
			</Title>
		);
	};

	return <>

		<MapDirectionsForm
			ref={directionsFormRef}
			data={location_details}
			onClose={handleGoBack}
			onSwap={handleSwap}
			onClick={onClickSelect}
			onChange={handleFormOnChange}
			// onClearField={handleClearField}
			options={locationsOptions}
			loading={loading_locations}
			onSelect={onSelect}
			focusedField={focusedField}
			startEnd={startEnd}
		// onKeyDown={handleOnKeyDown}
		// onKeyUp={handleOnKeyUp}
		// form={form}
		// onSwap={handleOnSwap}
		/>
		<div
			className="content-drawer-container"
			ref={drawerContainerRef}
		>
			<ContentDrawer
				nudge
				nudgeDistance={!location_details?.is_building || directions ? 30 : 15}
				loading={loading_directions || loading_details || loading_nearest}
				goBack={handleGoBack}
				title={renderDrawerTitle}
				handleLabel={loading_details ? <><LoadingOutlined /> Location</> : loading_directions ? <><LoadingOutlined /> Finding path</> : !loading_directions && directionsFormRef.current && directionsFormRef.current.getFieldValue('start') && directionsFormRef.current.getFieldValue('end') ? `From ${directionsFormRef.current.getFieldValue('start')} to ${directionsFormRef.current.getFieldValue('end')}` : location_details?.full_name || `${location_details?.name}, ${location_details?.building_short_name}`}
				drawerProps={{
					getContainer: () => drawerContainerRef.current
				}}
			>
				{!isMobile && null
				}
				<Title
					className="content-prompt opaque"
					level={5}
				>

					{!nearest_location && meta?.message ? (
						meta.message
					) : loading_directions ? (
						'Finding path...'
					) : (location_details || locationName) && !directions ? (
						<>
							{location_details.name}
							{location_details?.building_floor_name && location_details?.building_name && (
								<Text style={{ display: 'block', opacity: .75 }}>
									Floor {location_details?.building_floor_name}, {location_details?.building_name}
								</Text>
							)}
						</>
					) : (directions && !directions.paths && meta?.message) ? (
						meta.message
					) : null}

					{
						// loading_directions ? 'Finding path...' :
						// 	(directions && !directions.paths && meta?.message) && meta.message
					}
				</Title>

				<div className="hkust content">
					<div className="content-body">
						<div className="details">
							<div className="content-inner">
								{
									!loading_directions && (directions?.time && directions?.distance) ?
										<div className="head">
											<Space
												direction="vertical"
												size={'small'}
											>
												<Paragraph style={{ fontSize: 24, margin: 0, lineHeight: 'normal' }}>
													~{directions.time} min
													<Text style={{ display: 'block', fontSize: 14 }}>
														{directions.distance}m
													</Text>
												</Paragraph>
												{
													// The route is measured from where the user was when they asked,
													// so once they have walked a little this is how they move it on.
													usesCurrentLocation &&
													<Button
														type="link"
														size="small"
														loading={positioningLoading}
														onClick={handleRecalculate}
														style={{ padding: 0, height: 'auto' }}
														icon={
															<FontAwesomeIcon
																icon={faLocationCrosshairs}
																fontSize={12}
															/>
														}
													>
														Recalculate from my position
													</Button>
												}
											</Space>
										</div>
										: null
								}
								{
									(pathInfo && pathInfo.length) ?
										<div className="body">
											<List
												size="large"
												bordered
												dataSource={pathInfo}
												renderItem={renderListItem}
											/>
										</div>
										: null
								}

								<div
									className="path-options"
									style={{ marginBlockStart: directions ? 24 : 'unset', ...(isMobile && { order: 2 }) }}
								>
									<Title
										level={5}
										style={{ fontSize: 16, marginBlockEnd: 12 }}
									>
										Route Options
									</Title>

									<Space
										direction="vertical"
										style={{ width: '100%' }}
									>
										<Text style={{ fontSize: 16 }}>Accessibility</Text>
										<Checkbox.Group
											onChange={handleAccessibility}
											className="destination-accessibility"
											options={AccessibilityOptions}
											value={[
												...(accessibilityOptions.is_wheelchair_accessible ? ['is_wheelchair_accessible'] : []),
												...(accessibilityOptions.exclude_escalator ? ['exclude_escalator'] : [])
											]}
										/>
									</Space>

									{/* <Space
												direction="vertical"
												style={{ marginBlockStart: 12 }}
											>
												<Text>Time and distance</Text>
												<Radio.Group
													options={TimeAndDistanceOptions}
													style={{ display: 'flex', flexFlow: 'column', gap: 4 }}
												/>
											</Space> */}
								</div>
							</div>
						</div>
					</div>
				</div>
			</ContentDrawer>
		</div>

		{
			// meta &&
			// <div style={{ padding: '24px' }}>
			// 	<Paragraph>{meta}</Paragraph>
			// </div>
		}
	</>;
};

export default DirectionsPage;